import { html, css, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@ui5/webcomponents/dist/Select';
import '@ui5/webcomponents/dist/Option';
import '@ui5/webcomponents/dist/Label';
import '@ui5/webcomponents/dist/Button';
import { getCurrentApi } from '@kisters/wiski-web/api';

interface YearData {
  year: string;
  hasYearlyReport: boolean;
  months: string[];
}

@customElement('ww-hdoe-hydrochar')
export default class wwHdoeHydrochar extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: calc(100% - 30px);
    }
    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
    .left-panel {
      text-align: center;
      flex: 0;
      padding: 1rem;
      display: flex;
      gap: 1rem;
      justify-content: space-between;
      align-items: center;
    }

    .right-panel {
      flex: 1;
    }
    .msg {
      margin: auto;
    }
    .error {
      color: #d32f2f;
      margin: auto;
    }
    ui5-select {
      width: 180px;
    }
    
    .ios-pdf-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: 1rem;
      padding: 2rem;
      text-align: center;
    }
    
    .ios-pdf-message {
      font-size: 1.1rem;
      color: #666;
      margin-bottom: 1rem;
    }
    
    .download-button {
      background-color: #0066cc;
      color: white;
      padding: 12px 24px;
      border: none;
      border-radius: 8px;
      font-size: 1rem;
      cursor: pointer;
      text-decoration: none;
      display: inline-block;
      transition: background-color 0.3s;
    }
    
    .download-button:hover {
      background-color: #0052a3;
    }
    
    .alternative-viewers {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-top: 1rem;
    }
    
    .alternative-link {
      color: #0066cc;
      text-decoration: none;
      font-size: 0.9rem;
    }
    
    .alternative-link:hover {
      text-decoration: underline;
    }
  `;

  // Use custom map to provide customer locales
  private months = [
    'Jahrescharakteristik',
    'Jänner',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];

  private api = getCurrentApi();

  @state()
  accessor yearData: YearData[] = [];

  @property({ type: String })
  accessor path = '/documents';

  @state()
  accessor loading = true;

  @state()
  accessor error: string | null = null;

  @state()
  accessor selectedYear = '';

  @state()
  accessor selectedMonth = '';

  @property({ type: String })
  accessor selectedDoc = '';

  @state()
  accessor isIOS = false;

  async onAfterEnter() {
    this.detectIOS();
    this.fetchData();
  }

  private detectIOS() {
    // Detect iOS devices
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  private _getFullPath(file: string): string {
    const folder =
      file.length === 4 ? 'charakteristikJahr' : 'CharakteristikMonat';
    const formattedFile =
      file.length > 4 ? `${file.substring(0, 4)}-${file.substring(4)}` : file;
    const url = new URL(
      `${this.api.dataPath}${this.path}/${folder}/${formattedFile}.pdf`,
      window.location.origin,
    ).href;
    return url;
  }

  async fetchData() {
    this.loading = true;
    this.error = null;

    try {
      const year = await this.api._requestJson(
        `${this.path}/charakteristikJahr/index.json`,
      );
      const month = await this.api._requestJson(
        `${this.path}/CharakteristikMonat/index.json`,
      );

      // Create a map of years and their data
      const yearMap = new Map<string, YearData>();

      // Process yearly reports
      year?.data?.forEach(item => {
        const yearStr = item.name.substring(0, 4);
        yearMap.set(yearStr, {
          year: yearStr,
          hasYearlyReport: true,
          months: [],
        });
      });

      // Process monthly reports
      month?.data?.forEach(item => {
        const yearStr = item.name.substring(0, 4);
        const monthStr = item.name.substring(5, 7);

        if (!yearMap.has(yearStr)) {
          yearMap.set(yearStr, {
            year: yearStr,
            hasYearlyReport: false,
            months: [],
          });
        }

        const yearData = yearMap.get(yearStr)!;
        yearData.months.push(monthStr);
      });

      // Convert map to array and sort
      this.yearData = Array.from(yearMap.values())
        .map(data => ({
          ...data,
          months: data.months.sort(),
        }))
        .sort((a, b) => b.year.localeCompare(a.year));

      // Set initial values to most recent month
      if (this.yearData.length > 0) {
        const latestYear = this.yearData[0];
        if (latestYear.months.length > 0) {
          this.selectedYear = latestYear.year;
          this.selectedMonth = latestYear.months[latestYear.months.length - 1];
          this.selectedDoc = this._getFullPath(
            `${latestYear.year}${this.selectedMonth}`,
          );
        }
      }
    } catch (err) {
      const errorStr = err as string;
      console.error(errorStr);
      this.error =
        'Die Daten konnten leider nicht geladen werden. Bitte versuchen Sie es später erneut.';
      this.yearData = [];
      this.selectedDoc = '';
    } finally {
      this.loading = false;
    }
  }

  private _handleYearChange(e: CustomEvent) {
    const { selectedOption } = e.detail;
    if (!selectedOption?.value) return;
    this.selectedYear = selectedOption.value;
    // Find the year data for the newly selected year
    const yearData = this.yearData.find(y => y.year === this.selectedYear);
    if (yearData) {
      // If the current month exists in the new year data, keep it
      if (this.selectedMonth && yearData.months.includes(this.selectedMonth)) {
        // Month is valid, keep it
      } else if (yearData.hasYearlyReport) {
        // If yearly report exists, select it
        this.selectedMonth = '00';
      } else if (yearData.months.length > 0) {
        // Otherwise select the first available month (gets first value in month array, usually "01")
        [this.selectedMonth] = yearData.months;
      } else {
        // No months available, clear selection
        this.selectedMonth = '';
      }
    }
    this._updateSelectedDoc();
  }

  private _handleMonthChange(e: CustomEvent) {
    const { selectedOption } = e.detail;
    if (!selectedOption?.value) return;
    this.selectedMonth = selectedOption.value;
    this._updateSelectedDoc();
  }

  private _updateSelectedDoc() {
    if (!this.selectedYear) return;

    const yearData = this.yearData.find(y => y.year === this.selectedYear);
    if (!yearData) {
      return;
    }

    if (this.selectedMonth === '00') {
      // Yearly report
      if (yearData.hasYearlyReport) {
        this.selectedDoc = this._getFullPath(this.selectedYear);
      }
    } else if (this.selectedMonth) {
      // Monthly report
      if (yearData.months.includes(this.selectedMonth)) {
        this.selectedDoc = this._getFullPath(
          `${this.selectedYear}${this.selectedMonth}`,
        );
      }
    }
  }

  private _getDocumentName(): string {
    if (!this.selectedYear) return '';
    if (this.selectedMonth === '00') {
      return `Jahrescharakteristik ${this.selectedYear}`;
    }
    return `${this.months[parseInt(this.selectedMonth, 10)]} ${this.selectedYear}`;
  }

  private _downloadPdf() {
    if (!this.selectedDoc) return;
    
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = this.selectedDoc;
    link.download = `${this._getDocumentName()}.pdf`;
    link.target = '_blank';
    
    // Add download attribute for better compatibility
    link.setAttribute('download', `${this._getDocumentName()}.pdf`);
    
    // Trigger the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private _renderIOSPdfViewer() {
    return html`
      <div class="ios-pdf-container">
        <div class="ios-pdf-message">
          📄 ${this._getDocumentName()}
        </div>
        <div class="ios-pdf-message">
          Aufgrund von iOS-Beschränkungen kann das PDF nicht direkt angezeigt werden.
          Bitte verwenden Sie eine der folgenden Optionen:
        </div>
        
        <button class="download-button" @click="${this._downloadPdf}">
          📥 PDF herunterladen
        </button>
        
        <div class="alternative-viewers">
          <a 
            href="${this.selectedDoc}" 
            class="alternative-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            📋 In neuem Tab öffnen
          </a>
          
          <a 
            href="https://docs.google.com/viewer?url=${encodeURIComponent(this.selectedDoc)}"
            class="alternative-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            🔍 Mit Google Docs Viewer öffnen
          </a>
        </div>
        
        <div style="margin-top: 1rem; font-size: 0.8rem; color: #888;">
          Tipp: Nach dem Download können Sie das PDF in der Dateien-App öffnen
          oder in Apps wie Adobe Reader oder Apple Books importieren.
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`<div class="msg">Berichte werden geladen...</div>`;
    }

    if (this.error) {
      return html`<div class="error">${this.error}</div>`;
    }

    const selectedYearData = this.yearData.find(
      y => y.year === this.selectedYear,
    );
    const availableMonths = selectedYearData
      ? [
          ...(selectedYearData.hasYearlyReport ? ['00'] : []),
          ...selectedYearData.months,
        ]
      : [];

    return this.yearData.length
      ? html`
          <div class="left-panel">
            <ui5-label
              >Ausgewähltes Dokument: ${this._getDocumentName()}</ui5-label
            >
            <div>
              <ui5-select
                value="${this.selectedYear}"
                @change="${this._handleYearChange}"
                placeholder="Jahr auswählen"
              >
                ${this.yearData.map(
                  year =>
                    html`<ui5-option value="${year.year}"
                      >${year.year}</ui5-option
                    >`,
                )}
              </ui5-select>
              <ui5-select
                value="${this.selectedMonth}"
                @change="${this._handleMonthChange}"
                placeholder="Monat auswählen"
              >
                ${availableMonths.map(
                  month =>
                    html`<ui5-option
                      value="${month}"
                      ?selected="${month === this.selectedMonth}"
                      >${month === '00'
                        ? 'Jahrescharakteristik'
                        : this.months[parseInt(month, 10)]}</ui5-option
                    >`,
                )}
              </ui5-select>
            </div>
            <a
              href="https://www2.land-oberoesterreich.gv.at/internethydro/Monat/Start.jsp"
              target="_new"
              ><ui5-button>Archivdaten</ui5-button></a
            >
          </div>
          <div class="right-panel">
            ${this.isIOS 
              ? this._renderIOSPdfViewer()
              : html`<iframe
                  title="content"
                  id="contentframe"
                  src="${this.selectedDoc}"
                ></iframe>`
            }
          </div>
        `
      : html`<div class="msg">Aktuell sind keine Berichte vorhanden</div>`;
  }
}